import {UNLIMITED} from "@/constants";
import {Capability} from "@/api";
import {FormattedMessage} from "react-intl";
import {Limit, LimitError, LimitErrorType} from "./types";

import type {JSX} from "react";
import {isUnlimited, unlimitedToNumber} from "@/tools";

// !!!
// The functions below are separated to allow for easier testing.

export type CallQueueForExtension = {
    name: string;
    controllingLine: boolean;
};

// FIXME: we should probably drop all of this, and rely purely on backend validation

/**
 * Validates a single capability based on the content of the Limit object.
 *
 * @param capabilitySet Capability set to validate as a Limit object.
 * @param isSuperuser If superuser, automatic allocation increment is allowed.
 *
 * @returns An array of LimitError objects.
 */
export function validateCapabilitySet(capabilitySet: Limit, isSuperuser: boolean = false): LimitError[] {
    const errors: LimitError[] = [];

    if (
        !capabilitySet.user.allocated && // If the extensions does not have this capability set
        capabilitySet.form && // If the user wants to add this capability set
        !isSuperuser &&
        capabilitySet.domain?.used == capabilitySet.domain?.allocated
    ) {
        errors.push({
            name: `capability_set_names.${capabilitySet.name}`,
            type: LimitErrorType.ALLOCATION_EXCEEDED,
        });
    } else if (
        !capabilitySet.user.allocated &&
        capabilitySet.form &&
        isSuperuser &&
        capabilitySet.domain?.used == capabilitySet.domain?.allocated &&
        capabilitySet.system.allocated == capabilitySet.system.limit
    ) {
        errors.push({
            name: `capability_set_names.${capabilitySet.name}`,
            type: LimitErrorType.SYSTEM_LIMIT_EXCEEDED,
        });
    }

    return errors;
}

/**
 * Validates a single device type set based on the content of the Limit object.
 *
 * @param device The device to validate as a Limit object.
 * @param isSuperuser If superuser, automatic allocation increment is allowed.
 *
 * @returns An array of errors or null if valid.
 */
export function validateDeviceTypeSet(device: Limit, isSuperuser: boolean = false): LimitError[] {
    const errors: LimitError[] = [];

    if (device.up === UNLIMITED) return errors;

    if (device.up + device.form < device.user.used) {
        errors.push({
            name: `device_type_set_names.${device.name}`,
            type: LimitErrorType.DEVICE_LESS,
        });
    }

    if (!isSuperuser && device.form + (device.domain.used - device.user.allocated) > device.domain.allocated) {
        errors.push({
            name: `device_type_set_names.${device.name}`,
            type: LimitErrorType.DEVICE_MORE,
        });
    } else if (
        isSuperuser &&
        device.form > device.user.allocated && // always allow lowering the value
        device.form + (device.domain.used - device.user.allocated) >
            unlimitedToNumber(device.system.limit) - (device.system.allocated - device.domain.allocated)
    ) {
        errors.push({
            name: `device_type_set_names.${device.name}`,
            type: LimitErrorType.SYSTEM_LIMIT_EXCEEDED,
        });
    }

    return errors;
}

/**
 * Validate a capability that needs more checks.
 * Currently, there are only two capabilities that need more checks:
 * - Capability.CcAgent
 * - Capability.CcSupervisor
 *
 * (If more capabilities are added, this function should be updated.)
 *
 * @param capability The capability to validate.
 * @param capabilityIsUsed Whether the capability is used.
 * @param allowedCapabilities The capabilities that are allowed in the form.
 * @param groups The groups the extension is a member of. (Used to display the group name in the error message.)
 *
 * @returns A LimitError object or null if valid.
 */
export function validateUsedCapability(
    capability: Capability,
    capabilityIsUsed: boolean,
    allowedCapabilities: Capability[],
    groups: CallQueueForExtension[],
): LimitError | null {
    if (!capabilityIsUsed) return null;

    if (allowedCapabilities.includes(capability)) return null;

    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
    switch (capability) {
        case Capability.CcAgent:
            return {
                name: capability.toLowerCase(),
                type: LimitErrorType.AGENT,
                groups: groups.map((g) => g.name),
            } as LimitError;
        case Capability.CcSupervisor:
            return {
                name: capability.toLowerCase(),
                type: LimitErrorType.SUPERVISOR,
                groups: groups.filter((g) => g.controllingLine).map((g) => g.name),
            } as LimitError;
        default:
            return null;
    }
}

/**
 * Render a number or the word "unlimited".
 *
 * @param maybeUnlimited The number to render.
 * @param defaultValue If this is provided, this will be rendered if the `maybeUnlimited` is not unlimited.
 * @returns Either the number or the translated word "unlimited".
 */
export function renderUnlimitedValue(maybeUnlimited: number, defaultValue?: number): number | JSX.Element {
    defaultValue ??= maybeUnlimited;
    return isUnlimited(maybeUnlimited) ? <FormattedMessage id="unlimited" defaultMessage="Unlimited" /> : defaultValue;
}
